import React from 'react';
import { X, Copy } from 'lucide-react';

const SIGN_HI = ['मेष', 'वृषभ', 'मिथुन', 'कर्क', 'सिंह', 'कन्या', 'तुला', 'वृश्चिक', 'धनु', 'मकर', 'कुंभ', 'मीन'];

const BHAVA_NAMES = {
  1: 'तनु', 2: 'धन', 3: 'सहज', 4: 'सुख', 5: 'पुत्र', 6: 'रिपु',
  7: 'सप्तम (युवती)', 8: 'आयु', 9: 'धर्म', 10: 'कर्म', 11: 'लाभ', 12: 'व्यय',
};

const RASHI_LORDS_HI = ['मंगल', 'शुक्र', 'बुध', 'चंद्र', 'सूर्य', 'बुध',
  'शुक्र', 'मंगल', 'गुरु', 'शनि', 'शनि', 'गुरु'];

const copy = (txt) => navigator.clipboard?.writeText(String(txt));

const Shell = ({ title, subtitle, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid={testid}>
    <div
      className="relative w-full max-w-lg rounded-2xl border border-gold/40 bg-gradient-to-br from-purple-950 via-bg-primary to-purple-950 p-6 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <button data-testid="popup-close-btn" onClick={onClose} className="absolute top-4 right-4 text-text-muted hover:text-gold">
        <X className="w-5 h-5" />
      </button>
      <h3 className="font-heading text-2xl text-gold mb-1">{title}</h3>
      {subtitle && <p className="text-xs text-text-muted mb-4">{subtitle}</p>}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">{children}</div>
    </div>
  </div>
);

const Row = ({ label, value, copyable, mono, highlight }) => (
  <div className={`flex items-start justify-between gap-3 rounded-lg px-3 py-2 ${highlight ? 'bg-gold/10 border border-gold/30' : 'bg-white/5 border border-white/10'}`}>
    <div className="text-[11px] uppercase tracking-wider text-text-muted w-1/2">{label}</div>
    <div className={`text-sm flex-1 text-right ${mono ? 'font-mono' : ''} ${highlight ? 'text-gold font-semibold' : 'text-text-main'}`}>{value ?? '—'}</div>
    {copyable && value && (
      <button onClick={() => copy(value)} className="text-text-muted hover:text-gold flex-shrink-0">
        <Copy className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

// ── Planet Popup ────────────────────────────────────────────────────
export function PlanetPopup({ planet, data, onClose, onClickNakshatra }) {
  if (!planet || !data) return null;
  const p = data.popup_data?.[planet];
  if (!p) return null;

  const badges = [];
  if (p.status.retrograde) badges.push({ t: '↺ वक्री (Retrograde)', c: 'bg-amber-500/15 text-amber-300 border-amber-400/40' });
  if (p.status.combust) badges.push({ t: '🔥 अस्त (Combust)', c: 'bg-red-500/15 text-red-300 border-red-400/40' });
  if (p.status.exalted) badges.push({ t: '↑ उच्च (Exalted)', c: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40' });
  if (p.status.debilitated) badges.push({ t: '↓ नीच (Debilitated)', c: 'bg-rose-500/15 text-rose-300 border-rose-400/40' });
  if (p.status.own_sign) badges.push({ t: '⚜ स्वराशि', c: 'bg-gold/15 text-gold border-gold/40' });
  if (p.status.mooltrikona) badges.push({ t: '✦ मूलत्रिकोण', c: 'bg-indigo-500/15 text-indigo-300 border-indigo-400/40' });
  if (p.status.vargottama) badges.push({ t: '□ वर्गोत्तम', c: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/40' });

  return (
    <Shell title={p.name_hi} subtitle={`${planet} · ${p.sign_hi} · भाव ${p.house}`} onClose={onClose} testid="planet-popup">
      <Row label="Longitude (0–360°)" value={`${p.longitude.toFixed(4)}°`} mono copyable />
      <Row label="राशि" value={p.sign_hi} highlight />
      <Row label="अंश (DMS)" value={p.dms} mono copyable highlight />
      <Row label="भाव" value={p.house} highlight />
      <Row label="नक्षत्र" value={
        <button onClick={() => onClickNakshatra && onClickNakshatra(p.nakshatra)} className="text-gold hover:underline">
          {p.nakshatra}
        </button>
      } />
      <Row label="पाद" value={p.pada} />
      <Row label="नक्षत्र स्वामी" value={p.nakshatra_lord_hi} />
      <Row label="पाद स्वामी" value={p.pada_lord_hi} />
      <Row label="दैनिक गति (Daily Motion)" value={`${p.daily_motion_deg}° / ${p.motion_direction}`} mono />
      <Row label="गरिमा (Dignity)" value={p.status.sign_dignity} />

      <div className="pt-3">
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">स्थिति (Status)</div>
        <div className="flex flex-wrap gap-1.5">
          {badges.length === 0 ? <span className="text-xs text-text-muted">सामान्य</span> :
            badges.map((b, i) => (
              <span key={i} className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${b.c}`}>{b.t}</span>
            ))}
        </div>
      </div>
    </Shell>
  );
}

// ── Lagna Popup ─────────────────────────────────────────────────────
export function LagnaPopup({ data, onClose, onClickNakshatra }) {
  if (!data) return null;
  const l = data.lagna;
  return (
    <Shell title="लग्न (Ascendant)" subtitle={`${l.sign_hi} · ${l.dms}`} onClose={onClose} testid="lagna-popup">
      <Row label="लग्न राशि" value={l.sign_hi} highlight />
      <Row label="अंश (DMS)" value={l.dms} mono copyable highlight />
      <Row label="दशमलव अंश" value={`${l.degree_decimal}°`} mono />
      <Row label="नक्षत्र" value={
        <button onClick={() => onClickNakshatra && onClickNakshatra(l.nakshatra)} className="text-gold hover:underline">
          {l.nakshatra}
        </button>
      } />
      <Row label="पाद" value={l.pada} />
      <Row label="नक्षत्र स्वामी" value={l.nakshatra_lord_hi} />
      <Row label="पाद स्वामी" value={l.pada_lord_hi} />
      <Row label="राशि स्वामी (लग्नेश)" value={RASHI_LORDS_HI[l.sign_idx]} />
    </Shell>
  );
}

// ── House (Bhava) Popup ─────────────────────────────────────────────
export function HousePopup({ houseNum, data, onClose }) {
  if (!houseNum || !data) return null;
  const b = data.bhavas?.[String(houseNum)];
  if (!b) return null;
  // Planets located in this house
  const planetsInHouse = Object.entries(data.planetary_positions || {})
    .filter(([_, v]) => v.house === houseNum)
    .map(([nm, v]) => ({ name: nm, name_hi: v.name_hi, dms: v.dms }));

  return (
    <Shell title={`भाव ${b.number} · ${BHAVA_NAMES[b.number]}`} subtitle={`${b.sign_hi} राशि`} onClose={onClose} testid="house-popup">
      <Row label="भाव संख्या" value={b.number} highlight />
      <Row label="भाव नाम" value={BHAVA_NAMES[b.number]} />
      <Row label="राशि" value={b.sign_hi} highlight />
      <Row label="भावेश" value={b.bhava_lord_hi} />
      <Row label="प्रारंभ अंश" value={b.start_dms} mono />
      <Row label="मध्य अंश" value={b.middle_dms} mono />
      <Row label="अंत अंश" value={b.end_dms} mono />

      <div className="pt-3">
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">भाव में स्थित ग्रह</div>
        {planetsInHouse.length === 0 ? (
          <div className="text-sm text-text-muted">कोई ग्रह नहीं</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {planetsInHouse.map(p => (
              <span key={p.name} className="px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold text-sm">
                {p.name_hi} <span className="text-xs text-text-muted ml-1 font-mono">{p.dms}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}

// ── Nakshatra Popup ─────────────────────────────────────────────────
export function NakshatraPopup({ nakshatraIdx, nakshatraName, data, onClose }) {
  if ((!nakshatraIdx && nakshatraIdx !== 0) && !nakshatraName) return null;
  if (!data) return null;

  // Resolve index by name if needed
  let idx = nakshatraIdx;
  const wheel = data.nakshatra_wheel || {};
  const list = wheel.nakshatras || [];
  if ((idx === undefined || idx === null) && nakshatraName) {
    idx = list.findIndex(n => n.name_hi === nakshatraName || n.name_en === nakshatraName);
  }
  if (idx === undefined || idx === null || idx < 0) return null;

  const n = list[idx];
  if (!n) return null;

  // 4 padas → Navamsa sign per pada
  const PADA_NAVAMSA = [];
  for (let p = 1; p <= 4; p++) {
    const totalPada = idx * 4 + (p - 1);
    const navIdx = totalPada % 12;
    const startDeg = idx * (360 / 27) + (p - 1) * (360 / 108);
    const endDeg = startDeg + 360 / 108;
    PADA_NAVAMSA.push({ pada: p, navIdx, startDeg, endDeg });
  }

  return (
    <Shell title={n.name_hi} subtitle={`नक्षत्र #${n.number} · ${n.name_en}`} onClose={onClose} testid="nakshatra-popup">
      <Row label="नक्षत्र संख्या" value={n.number} highlight />
      <Row label="नक्षत्र स्वामी" value={n.lord_hi} highlight />
      <Row label="देवता" value={n.deity_hi} />
      <Row label="प्रतीक" value={n.symbol_hi} />
      <Row label="गण" value={n.gana_hi} />
      <Row label="योनि" value={n.yoni_hi} />
      <Row label="नाड़ी" value={n.nadi_hi} />
      <Row label="वर्ण" value={n.varna_hi} />
      <Row label="राज्जु" value={n.rajju_hi} />
      <Row label="तत्व" value={n.tattva_hi} />
      <Row label="लिंग" value={n.lingam_hi} />

      <div className="pt-3">
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">पाद विवरण</div>
        <table className="w-full text-xs">
          <thead className="text-gold/80">
            <tr>
              <th className="text-left py-1">चरण</th>
              <th className="text-left py-1">सीमा (अंश)</th>
              <th className="text-left py-1">नवांश राशि</th>
            </tr>
          </thead>
          <tbody>
            {PADA_NAVAMSA.map(p => (
              <tr key={p.pada} className="border-t border-white/5">
                <td className="py-1.5 text-gold font-semibold">पाद {p.pada}</td>
                <td className="py-1.5 font-mono text-text-muted">{p.startDeg.toFixed(2)}° – {p.endDeg.toFixed(2)}°</td>
                <td className="py-1.5">{SIGN_HI[p.navIdx]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}
